import React, { useState } from 'react';
import { Paper, Stack, Group, Text, Badge, Box, Tooltip, Button, Card, ThemeIcon, Indicator } from '@mantine/core';
import { IconCalendarEvent, IconChefHat, IconArrowRight, IconClock, IconUsers, IconChevronDown, IconChevronUp, IconPencil } from '@tabler/icons-react';
import { MealPlan, MealPlanAssignment, MealType, Recipe, getAssignmentMealType } from '../../types/mealPlan';
import { getAssignmentRecipe } from '../../utils/mealPlanHelpers';
import { pencilInImageFor } from '../../utils/pencilInImages';
import { formatDateRange, formatPlanMealsSummary } from '../../utils/mealPlanUtils';
import { formatTime } from '../../utils/formatTime';
import { RecipeImage } from '../UI/RecipeImage';
import { MealPlanCardMenu } from '../MealPlan/MealPlanCardMenu';

interface ActiveMealPlanCardProps {
  mealPlan: MealPlan;
  userId: string;
  onViewPlan: (plan: MealPlan) => void;
  onRecipeClick: (recipe: Recipe, mealPlanId?: string, assignmentId?: string) => void;
  onActivatePlan: (planId: string) => void;
  onDeletePlan?: (plan: MealPlan) => void;
  onCookRecipe?: (recipe: Recipe) => void;
  /** Passed through to the actions menu — see MealPlanCardMenu. */
  onPlanUpdated?: () => void;
  profilePublic?: boolean;
  calendarConnected?: boolean;
}

export const ActiveMealPlanCard: React.FC<ActiveMealPlanCardProps> = ({
  mealPlan,
  userId,
  onViewPlan,
  onRecipeClick,
  onActivatePlan,
  onDeletePlan,
  onCookRecipe,
  onPlanUpdated,
  profilePublic,
  calendarConnected
}) => {
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [activating, setActivating] = useState(false);

  // Today's date as YYYY-MM-DD in the viewer's local timezone
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  // Find today's meal assignment
  const getTodaysMeal = (): MealPlanAssignment | null => {
    if (!mealPlan.assignments || mealPlan.assignments.length === 0) {
      return null;
    }

    // Today's DINNER specifically: multi-slot plans put breakfast/lunch
    // assignments on the same date, and the dashboard hero is tonight's meal
    return (
      mealPlan.assignments.find(
        assignment =>
          assignment.plannedDate === todayStr &&
          getAssignmentMealType(assignment) === 'dinner'
      ) || null
    );
  };

  const todaysMeal = getTodaysMeal();

  // Today's breakfast and lunch for the "Also today" line under the hero:
  // the slots' one dashboard doorway while the hero stays tonight's dinner.
  // Names resolve like the plan grid (anchor recipe for reference days, the
  // penciled meal's own name otherwise).
  const otherSlotOrder: Record<MealType, number> = { breakfast: 0, lunch: 1, dinner: 2 };
  const todaysOtherMeals = (mealPlan.assignments ?? [])
    .filter(a => a.plannedDate === todayStr && getAssignmentMealType(a) !== 'dinner')
    .sort((a, b) => otherSlotOrder[getAssignmentMealType(a)] - otherSlotOrder[getAssignmentMealType(b)])
    .map(a => ({
      mealType: getAssignmentMealType(a),
      name: getAssignmentRecipe(a, mealPlan)?.name ?? a.customMeal?.name,
    }))
    .filter((m): m is { mealType: MealType; name: string } => !!m.name);
  const weekDinners = [...(mealPlan.assignments ?? [])]
    .filter(a => getAssignmentMealType(a) === 'dinner')
    .sort((a, b) => a.plannedDate.localeCompare(b.plannedDate));
  const todaysRecipe = todaysMeal ? getAssignmentRecipe(todaysMeal, mealPlan) : undefined;
  const isActive = mealPlan.status === 'active';
  
  const handleActivate = async () => {
    setActivating(true);
    onActivatePlan(mealPlan.id);
    // Don't need to reset activating since we'll navigate away
  };

  return (
    <Card 
      radius="xl"
      style={{ 
        background: isActive 
          ? 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)'
          : 'linear-gradient(135deg, #1E48A9 0%, #1d4ed8 100%)',
        border: 'none',
        overflow: 'visible',
        position: 'relative'
      }}
      p={0}
    >
      {/* Decorative Elements */}
      <Box
        style={{
          position: 'absolute',
          top: -10,
          right: -10,
          width: 40,
          height: 40,
          background: 'rgba(255, 255, 255, 0.2)',
          borderRadius: '50%',
          zIndex: 1
        }}
      />
      <Box
        style={{
          position: 'absolute',
          bottom: -5,
          left: -5,
          width: 30,
          height: 30,
          background: 'rgba(255, 255, 255, 0.15)',
          borderRadius: '50%',
          zIndex: 1
        }}
      />

      <Stack gap={0}>
        {/* Header Section */}
        <Box p="xl" pb="lg" style={{ position: 'relative', zIndex: 2 }}>
          <Group justify="space-between" align="flex-start">
            <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
              {/* nowrap: on narrow screens the title otherwise drops below
                  the icon; this keeps it beside the icon and wraps its own
                  words instead */}
              <Group gap="sm" align="center" wrap="nowrap">
                <ThemeIcon
                  size={48}
                  radius="xl"
                  variant="gradient"
                  gradient={isActive
                    ? { from: 'yellow', to: 'orange' }
                    : { from: 'indigo', to: 'blue' }
                  }
                  style={{ flexShrink: 0 }}
                >
                  <IconCalendarEvent size={24} />
                </ThemeIcon>
                <Text fw={700} size="xl" c="white">
                  {isActive ? "Today's featured meal" : "Today's meal available"}
                </Text>
              </Group>
              <Text size="sm" c="rgba(255,255,255,0.8)" fw={500} mb="xs">
                {formatDateRange(mealPlan)}
              </Text>
            </Stack>

            {/* Desktop action + kebab share the top-right corner; the mobile
                buttons below are full width and wrap onto their own line. */}
            <Group gap="xs" wrap="nowrap" align="flex-start">
              {isActive ? (
                <Button
                  variant="white"
                  color="orange"
                  size="sm"
                  rightSection={<IconArrowRight size={16} />}
                  onClick={() => onViewPlan(mealPlan)}
                  style={{ fontWeight: 600 }}
                  mb="xs"
                  visibleFrom="sm"
                >
                  View full plan
                </Button>
              ) : (
                <Button
                  variant="white"
                  color="indigo"
                  size="sm"
                  rightSection={activating ? undefined : <IconArrowRight size={16} />}
                  onClick={handleActivate}
                  loading={activating}
                  style={{ fontWeight: 600 }}
                  mb="xs"
                  disabled={activating}
                  visibleFrom="sm"
                >
                  {activating ? 'Activating...' : 'Activate Plan'}
                </Button>
              )}

              <MealPlanCardMenu
                mealPlan={mealPlan}
                userId={userId}
                onDeletePlan={onDeletePlan}
                onPlanUpdated={onPlanUpdated}
                profilePublic={profilePublic}
                calendarConnected={calendarConnected}
                onGradient
              />
            </Group>

            {isActive ? (
              /* Mobile View Full Plan Button */
              <Button
                variant="white"
                color="orange"
                size="sm"
                rightSection={<IconArrowRight size={16} />}
                onClick={() => onViewPlan(mealPlan)}
                style={{ fontWeight: 600 }}
                mb="xs"
                fullWidth
                hiddenFrom="sm"
              >
                View full plan
              </Button>
            ) : (
              /* Mobile Activate Button */
              <Button
                variant="white"
                color="indigo"
                size="sm"
                rightSection={activating ? undefined : <IconArrowRight size={16} />}
                onClick={handleActivate}
                loading={activating}
                style={{ fontWeight: 600 }}
                mb="xs"
                disabled={activating}
                fullWidth
                hiddenFrom="sm"
              >
                {activating ? 'Activating...' : 'Activate Plan'}
              </Button>
            )}
          </Group>
        </Box>

        {/* Main Content Section */}
        <Paper 
          p="xl" 
          pt="lg"
          radius={0}
          style={{ 
            borderTopLeftRadius: 'var(--mantine-radius-xl)',
            borderTopRightRadius: 'var(--mantine-radius-xl)',
            marginTop: -16,
            background: 'white',
            position: 'relative',
            zIndex: 2
          }}
        >
          {todaysMeal && todaysRecipe ? (
            <Stack gap="xl">
              {/* Today's Meal Hero Section */}
              
              {/* Mobile Layout - Stacked */}
              <Box hiddenFrom="md">
                <Stack gap="lg">
                  <Box pos="relative">
                    <Box
                      style={{ cursor: 'pointer', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', borderRadius: 'var(--mantine-radius-xl)', overflow: 'hidden' }}
                      onClick={() => onRecipeClick(todaysRecipe, mealPlan.id, todaysMeal.id)}
                    >
                      <RecipeImage
                        src={todaysRecipe.imageUrl}
                        alt={todaysRecipe.name}
                        w="100%"
                        h={240}
                      />
                    </Box>
                    <Badge 
                      variant="filled" 
                      color="green" 
                      size="sm"
                      style={{ 
                        position: 'absolute', 
                        top: 12, 
                        right: 12,
                        zIndex: 3,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                      }}
                    >
                      Today
                    </Badge>
                  </Box>

                  <Stack gap="md">
                    <Stack gap="xs">
                      <Text 
                        fw={700} 
                        size="xl" 
                        c="dark"
                        style={{ 
                          cursor: 'pointer',
                          lineHeight: 1.2
                        }}
                        onClick={() => onRecipeClick(todaysRecipe, mealPlan.id, todaysMeal.id)}
                      >
                        {todaysRecipe.name}
                      </Text>

                      <Group gap="lg">
                        <Group gap="xs">
                          <ThemeIcon size={24} radius="md" variant="light" color="orange">
                            <IconUsers size={14} />
                          </ThemeIcon>
                          <Text size="sm" fw={500} c="dark">
                            {todaysMeal.servings} {todaysMeal.servings === 1 ? 'serving' : 'servings'}
                          </Text>
                        </Group>
                        
                        <Group gap="xs">
                          <ThemeIcon size={24} radius="md" variant="light" color="blue">
                            <IconClock size={14} />
                          </ThemeIcon>
                          <Text size="sm" fw={500} c="dark">
                            {formatTime(todaysRecipe.totalTimeMinutes)}
                          </Text>
                        </Group>
                      </Group>
                    </Stack>

                    {todaysMeal.reasoning && (
                      <Text size="sm" c="dimmed" style={{ 
                        fontStyle: 'italic',
                        lineHeight: 1.4,
                        padding: '12px 16px',
                        background: 'var(--mantine-color-gray-0)',
                        borderRadius: 'var(--mantine-radius-md)',
                        borderLeft: '3px solid var(--mantine-color-orange-4)'
                      }}>
                        "{todaysMeal.reasoning}"
                      </Text>
                    )}

                    {isActive ? (
                      <Button 
                        variant="light" 
                        color="orange" 
                        size="md"
                        leftSection={<IconChefHat size={18} />}
                        onClick={() => onCookRecipe ? onCookRecipe(todaysRecipe) : onRecipeClick(todaysRecipe, mealPlan.id, todaysMeal.id)}
                        fullWidth
                        style={{ 
                          fontWeight: 600
                        }}
                      >
                        Cook this recipe
                      </Button>
                    ) : (
                      <Button 
                        variant="light" 
                        color="indigo" 
                        size="md"
                        leftSection={<IconArrowRight size={18} />}
                        onClick={() => onViewPlan(mealPlan)}
                        fullWidth
                        style={{ 
                          fontWeight: 600
                        }}
                      >
                        View plan
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Box>

              {/* Desktop Layout - Side by Side */}
              <Box visibleFrom="md">
                <Group gap="xl" align="flex-start">
                  <Box pos="relative">
                    <Box
                      style={{ cursor: 'pointer', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', borderRadius: 'var(--mantine-radius-xl)', overflow: 'hidden' }}
                      onClick={() => onRecipeClick(todaysRecipe, mealPlan.id, todaysMeal.id)}
                    >
                      <RecipeImage
                        src={todaysRecipe.imageUrl}
                        alt={todaysRecipe.name}
                        w={160}
                        h={160}
                      />
                    </Box>
                    <Badge 
                      variant="filled" 
                      color="green" 
                      size="sm"
                      style={{ 
                        position: 'absolute', 
                        top: -8, 
                        right: -8,
                        zIndex: 3,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                      }}
                    >
                      Today
                    </Badge>
                  </Box>

                  <Stack gap="md" style={{ flex: 1 }}>
                    <Stack gap="xs">
                      <Text 
                        fw={700} 
                        size="xl" 
                        c="dark"
                        style={{ 
                          cursor: 'pointer',
                          lineHeight: 1.2
                        }}
                        onClick={() => onRecipeClick(todaysRecipe, mealPlan.id, todaysMeal.id)}
                      >
                        {todaysRecipe.name}
                      </Text>

                      <Group gap="lg">
                        <Group gap="xs">
                          <ThemeIcon size={24} radius="md" variant="light" color="orange">
                            <IconUsers size={14} />
                          </ThemeIcon>
                          <Text size="sm" fw={500} c="dark">
                            {todaysMeal.servings} {todaysMeal.servings === 1 ? 'serving' : 'servings'}
                          </Text>
                        </Group>
                        
                        <Group gap="xs">
                          <ThemeIcon size={24} radius="md" variant="light" color="blue">
                            <IconClock size={14} />
                          </ThemeIcon>
                          <Text size="sm" fw={500} c="dark">
                            {formatTime(todaysRecipe.totalTimeMinutes)}
                          </Text>
                        </Group>
                      </Group>
                    </Stack>

                    {todaysMeal.reasoning && (
                      <Text size="sm" c="dimmed" style={{ 
                        fontStyle: 'italic',
                        lineHeight: 1.4,
                        padding: '12px 16px',
                        background: 'var(--mantine-color-gray-0)',
                        borderRadius: 'var(--mantine-radius-md)',
                        borderLeft: '3px solid var(--mantine-color-orange-4)'
                      }}>
                        "{todaysMeal.reasoning}"
                      </Text>
                    )}

                    {isActive ? (
                      <Button 
                        variant="light" 
                        color="orange" 
                        size="md"
                        leftSection={<IconChefHat size={18} />}
                        onClick={() => onCookRecipe ? onCookRecipe(todaysRecipe) : onRecipeClick(todaysRecipe, mealPlan.id, todaysMeal.id)}
                        style={{ 
                          alignSelf: 'flex-start',
                          fontWeight: 600
                        }}
                      >
                        Cook this recipe
                      </Button>
                    ) : (
                      <Button 
                        variant="light" 
                        color="indigo" 
                        size="md"
                        leftSection={<IconArrowRight size={18} />}
                        onClick={() => onViewPlan(mealPlan)}
                        style={{ 
                          alignSelf: 'flex-start',
                          fontWeight: 600
                        }}
                      >
                        View plan
                      </Button>
                    )}
                  </Stack>
                </Group>
              </Box>

              {todaysOtherMeals.length > 0 && (
                <Stack
                  gap={6}
                  style={{ cursor: 'pointer' }}
                  onClick={() => onViewPlan(mealPlan)}
                >
                  <Text size="xs" c="dimmed" fw={500}>
                    Also today
                  </Text>
                  {todaysOtherMeals.map((m) => (
                    <Group key={m.mealType} gap="xs" wrap="nowrap">
                      {/* Neutral gray slot badges, the Cook plan tab's language */}
                      <Badge variant="light" color="gray" size="sm" style={{ flexShrink: 0 }}>
                        {m.mealType === 'breakfast' ? 'Breakfast' : 'Lunch'}
                      </Badge>
                      <Text size="sm" c="dark" fw={500} truncate style={{ minWidth: 0 }}>
                        {m.name}
                      </Text>
                    </Group>
                  ))}
                </Stack>
              )}

              {/* This Week's Menu Preview */}
              {mealPlan.assignments && mealPlan.assignments.length > 1 && (
                <Stack gap="md" pt="md" style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}>
                  {/* Mobile Layout - Stacked */}
                  <Box hiddenFrom="lg">
                    <Group justify="space-between" align="center" mb="md">
                      <Group gap="xs" align="center">
                        <Text fw={600} size="md" c="dark">
                          This week's menu
                        </Text>
                        {!isActive && (
                          <Badge variant="light" color="indigo" size="xs">
                            Draft
                          </Badge>
                        )}
                      </Group>
                      <Badge variant="light" color="gray" size="sm">
                        {/* Dinner-scoped like the thumbnail strip beside it;
                            the raw assignment count triples on slot plans */}
                        {formatPlanMealsSummary(mealPlan)}
                      </Badge>
                    </Group>
                    
                    {mealPlan.summary && (
                      <Stack gap="xs" mb="md">
                        <Text size="sm" c="dimmed" style={{ lineHeight: 1.4 }}>
                          {summaryExpanded 
                            ? mealPlan.summary 
                            : mealPlan.summary.length > 100 
                              ? `${mealPlan.summary.substring(0, 135)}...`
                              : mealPlan.summary
                          }
                        </Text>
                        {mealPlan.summary.length > 100 && (
                          <Group 
                            gap="xs" 
                            style={{ cursor: 'pointer', width: 'fit-content' }} 
                            onClick={() => setSummaryExpanded(!summaryExpanded)}
                            mb="xs"
                          >
                            <Text size="xs" c="orange.6">
                              {summaryExpanded ? 'Show less' : 'Show more'}
                            </Text>
                            {summaryExpanded 
                              ? <IconChevronUp size={14} color="var(--mantine-color-orange-6)" /> 
                              : <IconChevronDown size={14} color="var(--mantine-color-orange-6)" />
                            }
                          </Group>
                        )}
                      </Stack>
                    )}
                    
                    <Group gap="sm" justify="center">
                      {weekDinners.slice(0, 7).map((assignment, index) => {
                        const recipe = getAssignmentRecipe(assignment, mealPlan);
                        if (!recipe) return null;
                        const isPrepDay = assignment.source === 'assignment_reference';
                        return (
                        <Tooltip
                          key={index}
                          label={<><strong>{assignment.plannedDay}:</strong> {recipe.name}</>}
                          position="top"
                          withArrow
                        >
                          <Indicator
                            disabled={assignment !== todaysMeal}
                            color={isActive ? "orange" : "indigo.4"}
                            size={16}
                            offset={-2}
                            position="top-end"
                          >
                            <Box
                              style={{
                                cursor: 'pointer',
                                opacity: assignment === todaysMeal ? 1 : 0.6,
                                filter: isPrepDay ? 'saturate(0.4) brightness(0.9)' : undefined,
                                transition: 'all 0.2s ease'
                              }}
                              onClick={() => onRecipeClick(recipe, mealPlan.id, assignment.id)}
                            >
                              <RecipeImage
                                src={recipe.imageUrl}
                                alt={recipe.name}
                                w={70}
                                h={70}
                                radius="md"
                                fallbackIconSize={24}
                              />
                            </Box>
                          </Indicator>
                        </Tooltip>
                        );
                      })}
                      {weekDinners.length > 7 && (
                        <Box
                          w={70}
                          h={70}
                          bg="gray.1"
                          style={{
                            borderRadius: 'var(--mantine-radius-md)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px dashed var(--mantine-color-gray-4)'
                          }}
                        >
                          <Text size="xs" c="dimmed" fw={600}>
                            +{weekDinners.length - 7}
                          </Text>
                        </Box>
                      )}
                    </Group>
                  </Box>

                  {/* Desktop Layout - Side by Side */}
                  <Box visibleFrom="lg">
                    <Group justify="space-between" align="flex-start" mb="md">
                      <Group gap="xs" align="center">
                        <Text fw={600} size="md" c="dark">
                          This week's menu
                        </Text>
                        {!isActive && (
                          <Badge variant="light" color="indigo" size="xs">
                            Draft
                          </Badge>
                        )}
                      </Group>
                      <Badge variant="light" color="gray" size="sm">
                        {/* Dinner-scoped like the thumbnail strip beside it;
                            the raw assignment count triples on slot plans */}
                        {formatPlanMealsSummary(mealPlan)}
                      </Badge>
                    </Group>
                    
                    <Group gap="xl" align="flex-start">
                      {/* Recipe Images */}
                      <Group gap="sm">
                        {weekDinners.slice(0, 7).map((assignment, index) => {
                          const recipe = getAssignmentRecipe(assignment, mealPlan);
                          if (!recipe) return null;
                          const isPrepDay = assignment.source === 'assignment_reference';
                          return (
                          <Tooltip
                            key={index}
                            label={<><strong>{assignment.plannedDay}:</strong> {recipe.name}</>}
                            position="top"
                            withArrow
                          >
                            <Indicator
                              disabled={assignment !== todaysMeal}
                              color={isActive ? "orange" : "indigo.4"}
                              size={16}
                              offset={-2}
                              position="top-end"
                            >
                              <Box
                                style={{
                                  cursor: 'pointer',
                                  opacity: assignment === todaysMeal ? 1 : 0.6,
                                  filter: isPrepDay ? 'saturate(0.4) brightness(0.9)' : undefined,
                                  transition: 'all 0.2s ease'
                                }}
                                onClick={() => onRecipeClick(recipe, mealPlan.id, assignment.id)}
                              >
                                <RecipeImage
                                  src={recipe.imageUrl}
                                  alt={recipe.name}
                                  w={70}
                                  h={70}
                                  radius="md"
                                  fallbackIconSize={24}
                                />
                              </Box>
                            </Indicator>
                          </Tooltip>
                          );
                        })}
                        {weekDinners.length > 7 && (
                          <Box
                            w={70}
                            h={70}
                            bg="gray.1"
                            style={{
                              borderRadius: 'var(--mantine-radius-md)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '1px dashed var(--mantine-color-gray-4)'
                            }}
                          >
                            <Text size="xs" c="dimmed" fw={600}>
                              +{weekDinners.length - 7}
                            </Text>
                          </Box>
                        )}
                      </Group>
                      
                      {/* Summary */}
                      {mealPlan.summary && (
                        <Stack gap="xs" style={{ flex: 1 }}>
                          <Text size="sm" c="dimmed" style={{ lineHeight: 1.4 }}>
                            {summaryExpanded 
                              ? mealPlan.summary 
                              : mealPlan.summary.length > 100 
                                ? `${mealPlan.summary.substring(0, 135)}...`
                                : mealPlan.summary
                            }
                          </Text>
                          {mealPlan.summary.length > 100 && (
                            <Group 
                              gap="xs" 
                              style={{ cursor: 'pointer', width: 'fit-content' }} 
                              onClick={() => setSummaryExpanded(!summaryExpanded)}
                            >
                              <Text size="xs" c="orange.6">
                                {summaryExpanded ? 'Show less' : 'Show more'}
                              </Text>
                              {summaryExpanded 
                                ? <IconChevronUp size={14} color="var(--mantine-color-orange-6)" /> 
                                : <IconChevronDown size={14} color="var(--mantine-color-orange-6)" />
                              }
                            </Group>
                          )}
                        </Stack>
                      )}
                    </Group>
                  </Box>
                </Stack>
              )}
            </Stack>
          ) : todaysMeal?.source === 'custom_meal' && todaysMeal.customMeal ? (
            (() => {
              // Same house image the plan view's custom meal card shows, so
              // tonight's penciled meal reads like a regular recipe hero:
              // full-width banner on mobile, square beside the text on desktop
              const { src: penciledSrc, focusY } = pencilInImageFor(todaysMeal.customMeal.name, getAssignmentMealType(todaysMeal));
              const penciledImage = (width: string | number, height: number) => (
                <Box
                  style={{ cursor: 'pointer', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', borderRadius: 'var(--mantine-radius-xl)', overflow: 'hidden' }}
                  onClick={() => onViewPlan(mealPlan)}
                >
                  <img
                    src={penciledSrc}
                    alt={todaysMeal.customMeal!.name}
                    style={{
                      width,
                      height,
                      objectFit: 'cover',
                      objectPosition: `50% ${focusY}%`,
                      display: 'block',
                    }}
                  />
                </Box>
              );
              const todayBadge = (top: number, right: number) => (
                <Badge
                  variant="filled"
                  color="green"
                  size="sm"
                  style={{
                    position: 'absolute',
                    top,
                    right,
                    zIndex: 3,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                  }}
                >
                  Today
                </Badge>
              );
              const penciledDetails = (
                <Stack gap="xs">
                  <Group gap="sm" align="center">
                    <Text
                      fw={700}
                      size="xl"
                      c="dark"
                      style={{ cursor: 'pointer', lineHeight: 1.2 }}
                      onClick={() => onViewPlan(mealPlan)}
                    >
                      {todaysMeal.customMeal.name}
                    </Text>
                    <Badge variant="light" color="grape" leftSection={<IconPencil size={12} />}>
                      Penciled in
                    </Badge>
                  </Group>
                  <Text size="sm" c="dimmed">
                    You penciled this in for tonight. No recipe needed.
                  </Text>
                  {todaysMeal.customMeal.note && (
                    <Text size="sm" c="dimmed" style={{ fontStyle: 'italic' }}>
                      "{todaysMeal.customMeal.note}"
                    </Text>
                  )}
                </Stack>
              );
              return (
                <>
                  {/* Mobile: stacked banner, like the recipe hero */}
                  <Box hiddenFrom="md">
                    <Stack gap="lg">
                      <Box pos="relative">
                        {penciledImage('100%', 240)}
                        {todayBadge(12, 12)}
                      </Box>
                      {penciledDetails}
                    </Stack>
                  </Box>
                  {/* Desktop: square image beside the text, like the recipe hero */}
                  <Box visibleFrom="md">
                    <Group gap="xl" align="flex-start">
                      <Box pos="relative">
                        {penciledImage(160, 160)}
                        {todayBadge(-8, -8)}
                      </Box>
                      <Box style={{ flex: 1 }}>{penciledDetails}</Box>
                    </Group>
                  </Box>
                </>
              );
            })()
          ) : (
            <Stack gap="lg" align="center" py="xl">
              <ThemeIcon size={60} radius="xl" variant="light" color="gray">
                <IconChefHat size={30} />
              </ThemeIcon>
              <Stack gap="xs" align="center">
                <Text fw={600} size="lg" c="dark">
                  No meal planned for today
                </Text>
                <Text size="sm" c="dimmed" ta="center">
                  Check your meal plan for upcoming recipes or plan a new meal.
                </Text>
              </Stack>
              <Button 
                variant="light" 
                color="orange"
                onClick={() => onViewPlan(mealPlan)}
              >
                View full plan
              </Button>
            </Stack>
          )}
        </Paper>
      </Stack>
    </Card>
  );
};
